import { useMemo, useState, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { PerspectiveCamera, Scene, WebGLRenderTarget, Vector2 } from 'three';
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  ChromaticAberrationEffect,
  PixelationEffect,
  GlitchEffect,
  GlitchMode
} from 'postprocessing';

import { useGlitchContext, useTeamMemberContext } from 'context/Team';
import useWindowSize from 'hooks/useWindowSize';

interface ITransition {
  cam: PerspectiveCamera;
  scene: Scene;
  renderTarget: WebGLRenderTarget;
}

enum GLITCH_PARAMS {
  MIN_DELAY = 0.0,
  MAX_DELAY = 2.343,
  MIN_DURATION = 0.2,
  MAX_DURATION = 1.313,
  GLITCH_RATIO = 0.085,
  COLUMNS = 0
}

export function useTransition({ cam, scene, renderTarget }: ITransition) {
  const { gl } = useThree();
  const {
    config: { currentTeamMember }
  } = useTeamMemberContext();
  const {
    config: { fetchGlitchVal }
  } = useGlitchContext();
  const { width } = useWindowSize();
  const [glitchVal, setGlitchVal] = useState(0.0);
  const [pixelationVal, setPixelationVal] = useState<number>(0);

  useFrame(() => {
    if (fetchGlitchVal && currentTeamMember) {
      const newGlitchVal = fetchGlitchVal();
      if (glitchVal !== newGlitchVal) {
        setGlitchVal(newGlitchVal);
      }
    }
  });

  useEffect(() => {
    if (width) {
      // 992 is the 'lg' breakpoint width value
      if (width > 992) setPixelationVal(8);
      else setPixelationVal(5);
    }
  }, [width]);

  return useMemo(() => {
    const renderScene = new RenderPass(scene, cam);

    const comp = new EffectComposer(gl, renderTarget);
    comp.autoRenderToScreen = false;
    comp.addPass(renderScene);

    const chromaticAbberationPass = new EffectPass(
      cam,
      new ChromaticAberrationEffect({
        radialModulation: true,
        modulationOffset: 0.2,
        offset: new Vector2(0.018, 0.01)
      })
    );
    chromaticAbberationPass.enabled = currentTeamMember ? true : false;
    comp.addPass(chromaticAbberationPass);

    const pixelationPass = new EffectPass(cam, new PixelationEffect(pixelationVal));
    comp.addPass(pixelationPass);

    const glitchEffect = new GlitchEffect({
      delay: new Vector2(GLITCH_PARAMS.MIN_DELAY, GLITCH_PARAMS.MAX_DELAY),
      duration: new Vector2(GLITCH_PARAMS.MIN_DURATION, GLITCH_PARAMS.MAX_DURATION),
      strength: new Vector2(
        glitchVal * 0.25,
        glitchVal
        // glitchValToo * 0.25, glitchValToo
        // glitchValget() * 0.25,
        // glitchValget()
      ),

      ratio: GLITCH_PARAMS.GLITCH_RATIO,
      columns: GLITCH_PARAMS.COLUMNS
    });
    glitchEffect.mode = GlitchMode.CONSTANT_MILD;
    const glitchPass = new EffectPass(cam, glitchEffect);
    comp.addPass(glitchPass);

    return [comp];
  }, [
    scene,
    cam,
    gl,
    renderTarget,
    currentTeamMember,
    glitchVal,
    // glitchValToo,
    // glitchValChanged,
    pixelationVal
  ]);
}

const Transition = {};

export default Transition;
